import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import {
  CommunityBoundary,
  MonitoringConfiguration,
  MonitoringConfigurationInput,
} from "src/Types/monitoring.types";
import {
  MonitoringAlert as MonitoringAlertType,
  MonitoringAlertFeed,
} from "src/Types/alerts.types";
import {
  getMonitoringConfiguration,
  saveMonitoringConfiguration,
} from "src/utils/apicalls";
import MonitoringMap from "./MonitoringMap";
import UpgradeModal from "./UpgradeModal";
import AlertsSidebar from "./AlertsSidebar";
import AlertsBottomSheet from "./AlertsBottomSheet";
import LocationSetupDrawer from "./LocationSetupDrawer";
import LocationSetupPanel from "./LocationSetupPanel";
import MonitoringToolbar from "./MonitoringToolbar";
import WorkspaceSwitcher from "./WorkspaceSwitcher";
import { useAuth } from "src/providers/AuthProvider";
import { setActiveWorkspaceKey } from "src/utils/activeWorkspace";
import { getApiErrorMessage } from "src/utils/apiError";

type PendingAction = { community: CommunityBoundary } | null;

const emptyConfiguration = (): MonitoringConfigurationInput => ({
  mode: "COMMUNITY",
  community: null,
  addresses: [],
});

const toInput = (
  configuration: MonitoringConfiguration
): MonitoringConfigurationInput => ({
  mode: configuration.mode,
  community: configuration.community,
  addresses: configuration.addresses,
});

interface DashboardWorkspaceProps {
  canEdit: boolean;
  workspaceSwitcher?: React.ReactNode;
}

const DashboardWorkspace: React.FC<DashboardWorkspaceProps> = ({
  canEdit,
  workspaceSwitcher,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [draft, setDraft] = useState<MonitoringConfigurationInput>(
    emptyConfiguration()
  );
  const [savedSnapshot, setSavedSnapshot] = useState(
    JSON.stringify(emptyConfiguration())
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    severity: "success" | "error";
    text: string;
  } | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [savedConfigurationId, setSavedConfigurationId] = useState<string | null>(
    null
  );
  const [monitoringAlerts, setMonitoringAlerts] = useState<MonitoringAlertType[]>(
    []
  );
  const [selectedAlert, setSelectedAlert] =
    useState<MonitoringAlertType | null>(null);
  const [alertSelectionSource, setAlertSelectionSource] = useState<
    "map" | "sidebar" | null
  >(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [locationDrawerOpen, setLocationDrawerOpen] = useState(false);

  useEffect(() => {
    let active = true;
    getMonitoringConfiguration()
      .then((configuration) => {
        if (!active || !configuration) return;
        const input = toInput(configuration);
        setDraft(input);
        setSavedSnapshot(JSON.stringify(input));
        setSavedConfigurationId(configuration.id);
      })
      .catch((error) => {
        if (active) {
          setMessage({
            severity: "error",
            text: getApiErrorMessage(error, t, "dashboard.setup.loadError"),
          });
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [t]);

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== savedSnapshot,
    [draft, savedSnapshot]
  );
  const canSave = isDirty && Boolean(draft.community);

  const handleCommunitySelect = (community: CommunityBoundary) => {
    if (
      draft.community &&
      draft.community.providerId !== community.providerId
    ) {
      setPendingAction({ community });
      return;
    }
    setMessage(null);
    setDraft({ mode: "COMMUNITY", community, addresses: [] });
  };

  const confirmPendingAction = () => {
    const action = pendingAction;
    setPendingAction(null);
    if (!action) return;
    setDraft({ mode: "COMMUNITY", community: action.community, addresses: [] });
    setMessage(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const configuration = await saveMonitoringConfiguration(draft);
      const input = toInput(configuration);
      setDraft(input);
      setSavedSnapshot(JSON.stringify(input));
      setSavedConfigurationId(configuration.id);
      setMessage({
        severity: "success",
        text: t("dashboard.setup.saved"),
      });
      setLocationDrawerOpen(false);
    } catch (error) {
      setMessage({
        severity: "error",
        text: getApiErrorMessage(error, t, "dashboard.setup.saveError"),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFeedChange = useCallback((feed: MonitoringAlertFeed) => {
    setMonitoringAlerts(feed.alerts);
    setUnreadCount(feed.unreadCount);
  }, []);

  const resetDraftToSaved = () => {
    setDraft(JSON.parse(savedSnapshot));
    setMessage(null);
  };

  const openLocationDrawer = () => {
    resetDraftToSaved();
    setLocationDrawerOpen(true);
  };

  const cancelLocationDrawer = () => {
    resetDraftToSaved();
    setLocationDrawerOpen(false);
  };

  const privateRobots = (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
        {privateRobots}
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography color="text.secondary">{t("dashboard.setup.loading")}</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        ...(savedConfigurationId
          ? { height: "100%", overflow: "hidden", minHeight: 0 }
          : { minHeight: "calc(100dvh - 80px)" }),
      }}
    >
      {privateRobots}
      {!savedConfigurationId && !canEdit ? (
        <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 6, md: 10 } }}>
          <Stack
            spacing={1.5}
            alignItems="center"
            textAlign="center"
            sx={{ maxWidth: 520, mx: "auto" }}
          >
            <Typography variant="h4">{t("dashboard.setup.almostReadyTitle")}</Typography>
            <Typography color="text.secondary">
              {t("dashboard.setup.almostReadyBody")}
            </Typography>
            {message && <Alert severity={message.severity}>{message.text}</Alert>}
          </Stack>
        </Box>
      ) : !savedConfigurationId ? (
        <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>
          <Box sx={{ maxWidth: 1560, mx: "auto" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={2}
              mb={3}
            >
              <Box>
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={t("dashboard.setup.chip")}
                  sx={{ mb: 1.25 }}
                />
                <Typography variant="h4">{t("dashboard.setup.title")}</Typography>
                <Typography color="text.secondary" mt={0.5}>
                  {t("dashboard.setup.subtitle")}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {workspaceSwitcher}
                <Chip
                  icon={isDirty ? undefined : <CheckCircleIcon />}
                  color={isDirty ? "warning" : "success"}
                  label={isDirty ? t("dashboard.setup.unsavedChanges") : t("dashboard.setup.savedChip")}
                  variant="outlined"
                />
              </Stack>
            </Stack>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "minmax(360px, 430px) 1fr" },
                gap: 3,
                alignItems: "start",
              }}
            >
              <LocationSetupPanel
                draft={draft}
                message={message}
                saving={saving}
                canSave={canSave}
                onCommunitySelect={handleCommunitySelect}
                onUpgrade={() => setUpgradeOpen(true)}
                onSave={handleSave}
              />
              <MonitoringMap
                mode={draft.mode}
                community={draft.community}
                addresses={draft.addresses}
                alerts={[]}
                selectedAlertId={null}
                onSelectAlert={() => undefined}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <MonitoringToolbar
            configuration={draft}
            isDirty={isDirty}
            onChangeLocation={canEdit ? openLocationDrawer : undefined}
            workspaceSwitcher={workspaceSwitcher}
          />
          <Box
            sx={{
              height: { xs: "calc(100% - 68px)", sm: "calc(100% - 72px)" },
              minHeight: 0,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr minmax(330px, 380px)" },
              gap: { xs: 0, md: 1.5 },
              p: { xs: 0, md: 1.5 },
              pb: { xs: "92px", md: 1.5 },
            }}
          >
            <MonitoringMap
              ref={mapContainerRef}
              mode={draft.mode}
              community={draft.community}
              addresses={draft.addresses}
              alerts={monitoringAlerts}
              selectedAlertId={selectedAlert?.id || null}
              onSelectAlert={(alert) => {
                setSelectedAlert(alert);
                setAlertSelectionSource(alert ? "map" : null);
              }}
              fullBleed
            />
            {!isCompact && (
              <AlertsSidebar
                enabled
                mapContainerRef={mapContainerRef}
                configuration={draft}
                canScan={canEdit}
                selectedAlertId={selectedAlert?.id || null}
                onSelectAlert={(alert) => {
                  setSelectedAlert(alert);
                  setAlertSelectionSource("sidebar");
                }}
                onFeedChange={handleFeedChange}
              />
            )}
          </Box>
          {isCompact && (
            <AlertsBottomSheet
              unreadCount={unreadCount}
              selectedAlertId={selectedAlert?.id || null}
              selectionSource={alertSelectionSource}
            >
              <AlertsSidebar
                enabled
                mapContainerRef={mapContainerRef}
                configuration={draft}
                canScan={canEdit}
                variant="embedded"
                selectedAlertId={selectedAlert?.id || null}
                onSelectAlert={(alert) => {
                  setSelectedAlert(alert);
                  setAlertSelectionSource("sidebar");
                }}
                onFeedChange={handleFeedChange}
              />
            </AlertsBottomSheet>
          )}
          <LocationSetupDrawer
            open={canEdit && locationDrawerOpen}
            onCancel={cancelLocationDrawer}
          >
            <LocationSetupPanel
              compact
              draft={draft}
              message={message}
              saving={saving}
              canSave={canSave}
              onCommunitySelect={handleCommunitySelect}
              onUpgrade={() => setUpgradeOpen(true)}
              onSave={handleSave}
            />
          </LocationSetupDrawer>
        </Box>
      )}

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />

      <Dialog
        open={Boolean(pendingAction)}
        onClose={() => setPendingAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("dashboard.setup.replaceTitle")}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            {t("dashboard.setup.replaceBody")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setPendingAction(null)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={confirmPendingAction}>
            {t("dashboard.setup.replaceConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Dashboard: React.FC = () => {
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const cityKey = isAdmin ? searchParams.get("city") : null;

  // Set before the workspace mounts so its first requests target the chosen city.
  setActiveWorkspaceKey(cityKey);
  useEffect(() => () => setActiveWorkspaceKey(null), []);

  const handleWorkspaceChange = (workspaceKey: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (workspaceKey) {
      params.set("city", workspaceKey);
    } else {
      params.delete("city");
    }
    setSearchParams(params);
  };

  return (
    <DashboardWorkspace
      key={cityKey ?? "own"}
      canEdit={isAdmin}
      workspaceSwitcher={
        isAdmin ? (
          <WorkspaceSwitcher value={cityKey} onChange={handleWorkspaceChange} />
        ) : undefined
      }
    />
  );
};

export default Dashboard;
